import I18n from "i18n-js";
import React, { Children, useMemo } from "react";
import ReactIs from "react-is";
import {
  arrayOf,
  bool,
  element,
  func,
  node,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";
import classnames from "classnames";
import Pagination from "react-bootstrap/Pagination";

StatelessTable.propTypes = {
  actions: oneOfType([element, arrayOf(element)]),
  data: arrayOf(shape).isRequired,
  headData: arrayOf(oneOfType([string, element])),
  columnStyle: arrayOf(string),
  pagination: bool,
  manualPagination: bool,
  dataCount: number,
  currentPage: number,
  onPageChanged: func,
  paginationAside: node,
  rowData: func.isRequired,
  filters: oneOfType([element, arrayOf(element)]),
  emptyContent: string,
  isActiveRow: func,
  getRowClasses: func,
  className: string,
  perPage: number,
  tableClasses: string,
  inProgress: bool,
  filtersInProgress: bool,
  skipChunk: bool,
};

export default function StatelessTable({
  actions = null,
  data,
  headData = [],
  columnStyle = [],
  pagination = false,
  currentPage = 1,
  onPageChanged = null,
  paginationAside = null,
  manualPagination = false,
  dataCount = 0,
  rowData,
  filters = null,
  emptyContent = null,
  isActiveRow = () => true,
  getRowClasses = () => [],
  className = "col-12",
  perPage = 50,
  tableClasses = null,
  inProgress = false,
  filtersInProgress = false,
  skipChunk = false,
}) {
  const chunkedData = useMemo(() => skipChunk ? [data] : chunk(data, perPage), [perPage, data, skipChunk]);
  const currentData = useMemo(
    () =>
      chunkedData[manualPagination ? 0 : Math.max(currentPage - 1, 0)] ?? [],
    [chunkedData, currentPage, manualPagination],
  );

  const renderFilters = () => {
    let filtersArray = filters;
    // Check if filters is a Fragment and fetch its children:
    if (filters && ReactIs.isFragment(filters)) {
      filtersArray = Children.toArray(filters.props.children);
    }
    // No filters:
    if (!filtersArray || !filtersArray.length) {
      return null;
    }

    return (
      <form className="filter">
        <span className="title">{I18n.t("common.filter")}</span>
        <div className="row">
          {filtersInProgress ? (
            <div className="loader" />
          ) : (
            filtersArray.map((filter, index) => (
              <div key={"filter".concat(index)} className="col-lg-2 col-md-12">
                {filter}
              </div>
            ))
          )}
        </div>
      </form>
    );
  };

  /**
   * @param {Object} event
   * @param {Number} newPage
   */
  const onPaginate = (event, newPage) => {
    event.preventDefault();

    onPageChanged && onPageChanged(newPage);
  };

  /**
   * Render table head
   */
  const renderHead = () => {
    if (!headData.length) {
      return null;
    }

    return (
      <tr>
        {headData.map((item, index) => (
          <th className={columnStyle[index] || ""} key={index}>
            {item}
          </th>
        ))}
      </tr>
    );
  };

  /**
   * Render each table row
   */
  const renderRows = () => {
    return currentData.map((row, index) => {
      const rowClasses = classnames({ "table-active": !isActiveRow(row)}, getRowClasses(row));

      return (
        <tr className={rowClasses} key={index}>
          {rowData(row, index).map((column, rowIndex) => (
            <td className={columnStyle[rowIndex] || ""} key={rowIndex}>
              {column}
            </td>
          ))}
        </tr>
      );
    });
  };

  /**
   * Render pagination
   */
  const renderPagination = () => {
    const pageCount = manualPagination
      ? Math.ceil(dataCount / perPage)
      : chunkedData.length;

    if (pageCount < 2) {
      return null;
    }

    const paginationItems = computePaginationItems(pageCount);

    return (
      <Pagination>
        <Pagination.First
          onClick={(event) => onPaginate(event, 1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={(event) => onPaginate(event, currentPage - 1)}
          disabled={currentPage === 1}
        />
        {paginationItems}
        <Pagination.Next
          onClick={(event) => onPaginate(event, currentPage + 1)}
          disabled={currentPage === pageCount}
        />
        <Pagination.Last
          onClick={(event) => onPaginate(event, pageCount)}
          disabled={currentPage === pageCount}
        />
      </Pagination>
    );
  };

  const computePaginationItems = (pageCount) => {
    const startRange = 3;
    const maxDisplayedPages = 3;
    const endRange = 3;

    const data = [];

    if (pageCount <= maxDisplayedPages) {
      for (let i = 1; i <= pageCount; i++) {
        data.push(
          <Pagination.Item
            active={i === currentPage}
            key={"page-item".concat(i)}
            onClick={(event) => onPaginate(event, i)}
          >
            {i}
          </Pagination.Item>,
        );
      }
    } else {
      const leftSide = currentPage - Math.floor(maxDisplayedPages / 2) > 1;
      const rightSide =
        currentPage + Math.round(maxDisplayedPages / 2) < pageCount;
      const str = Math.max(1, Math.round(currentPage - maxDisplayedPages / 2));
      const end = Math.min(
        pageCount,
        Math.round(currentPage + maxDisplayedPages / 2),
      );
      if (str > 1) {
        for (let i = 1; i <= Math.min(startRange, str - 1); i++) {
          data.push(
            <Pagination.Item
              active={i === currentPage}
              key={"page-item".concat(i)}
              onClick={(event) => onPaginate(event, i)}
            >
              {i}
            </Pagination.Item>,
          );
        }
      }
      if (leftSide) {
        data.push(<Pagination.Ellipsis key="leftEllipsis" />);
      }
      for (let i = str; i <= end; i++) {
        data.push(
          <Pagination.Item
            key={i}
            active={i === currentPage}
            onClick={(event) => onPaginate(event, i)}
          >
            {i}
          </Pagination.Item>,
        );
      }
      if (rightSide) {
        data.push(<Pagination.Ellipsis key="rightEllipsis" />);
      }
      if (end < pageCount) {
        // data.push(<Pagination.Ellipsis key="rightEllipsis" />);
        for (
          let i = Math.max(pageCount - endRange, end + 1);
          i <= pageCount;
          i++
        ) {
          data.push(
            <Pagination.Item
              active={i === currentPage}
              key={"page-item".concat(i)}
              onClick={(event) => onPaginate(event, i)}
            >
              {i}
            </Pagination.Item>,
          );
        }
      }
    }
    return data;
  };

  /**
   * Render table (content)
   */
  const renderTable = () => {
    if (inProgress) {
      return <div className="loader" />;
    }
    if (!currentData.length) {
      return <p>{emptyContent || I18n.t("common.no_data")}</p>;
    }

    return (
      <div className="table-responsive table-wrap">
        <table className={classnames("table", tableClasses)}>
          <thead>{renderHead()}</thead>
          <tbody>{renderRows()}</tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={`${className} table-component`}>
      {renderFilters()}
      {(pagination || actions || paginationAside) && (
        <div className="btn-toolbar align-items-center">
          {pagination && <div className="col-auto">{renderPagination()}</div>}
          {paginationAside && (
            <div className="pagination-aside col-auto">{paginationAside}</div>
          )}
          {actions && <div className="col text-end">{actions}</div>}
        </div>
      )}
      {inProgress ? <div className="loader" /> : renderTable()}
      {pagination && renderPagination()}
    </div>
  );
}

/**
 * Chunk array method
 *
 * @param {Array} source
 * @param {Number} length
 *
 * @return {Array}
 */
function chunk(source, length) {
  if (source.length === 0) {
    return [[]];
  }

  const chunkedData = [];
  const data = source.slice(0);

  while (data.length > 0) {
    chunkedData.push(data.splice(0, length));
  }

  return chunkedData;
}
